import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { TuiAlertService } from '@taiga-ui/core';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import {
  RouteNode,
  ServiceDetail,
  UpdateAssignedTimeRequest,
  UpdateResponse,
} from 'src/app/models/service-detail/service-detail.model';
import { Zone, ZonesResponse } from 'src/app/models/zone/zone.model';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, filter, finalize, takeUntil } from 'rxjs/operators';
import * as moment from 'moment-timezone';
import { hhmmPattern, hhmmValidator } from 'src/app/utils';

@Component({
  selector: 'app-modal-gestion-node',
  templateUrl: './modal-gestion-node.component.html',
  styleUrls: ['./modal-gestion-node.component.css'],
})
export class ModalGestionNodeComponent implements OnInit, OnDestroy {
  @Input() orderHash = '';
  @Input() serviceInfo!: ServiceDetail;
  @Input() nodeInfo!: RouteNode;
  @Output() updated = new EventEmitter<boolean>();

  assignForm!: FormGroup;
  zones: Zone[] = [];
  loading = false;

  private initialDurationMinutes = 30;
  private timeZone!: string;
  private destroy$ = new Subject<void>();

  constructor(
    private alerts: TuiAlertService,
    private fb: FormBuilder,
    private offcanvasService: NgbOffcanvas,
    private hs: HistoryServicesService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadZones();
    this.setDefaultTimes();
    this.calculateInitialDuration();
    this.watchStartHourChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.assignForm = this.fb.group(
      {
        nodeDate: ['', Validators.required],
        nodeStartHour: ['', [Validators.required, hhmmValidator]],
        nodeEndHour: ['', [Validators.required, hhmmValidator]],
      },
      {
        validators: this.startBeforeEndValidator.bind(this),
      }
    );
  }

  private loadZones(): void {
    this.hs
      .getZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: ZonesResponse) => {
          this.zones = resp.zones || [];
        },
        error: (err) => console.error('Error loading zones', err),
      });
  }

  private watchStartHourChanges(): void {
    this.assignForm
      .get('nodeStartHour')!
      .valueChanges.pipe(
        debounceTime(200),
        filter((v: string) => hhmmPattern.test(v)),
        takeUntil(this.destroy$)
      )
      .subscribe((start) => this.applyDurationToEnd(start));
  }

  private calculateInitialDuration(): void {
    const { nodeStartHour, nodeEndHour } = this.assignForm.value;
    const fmt = 'HH:mm';
    const m1 = moment(nodeStartHour, fmt);
    const m2 = moment(nodeEndHour, fmt);
    const diff = m2.diff(m1, 'minutes');
    this.initialDurationMinutes = diff > 0 ? diff : 30;
  }

  private setDefaultTimes(): void {
    const sd = this.serviceInfo.serviceDates;
    this.timeZone =
      sd.serviceAssignedPickupDate?.timeZone ??
      sd.servicePickupDateRangeStart.timeZone;

    const rawStartIso =
      this.nodeInfo.nodeStartHour?.completeDate ??
      sd.serviceAssignedPickupDate?.completeDate ??
      sd.servicePickupDateRangeStart.completeDate;

    const cleanedStart = rawStartIso.split(' ')[0];
    const startM = moment.tz(cleanedStart, this.timeZone);

    const rawEndIso =
      this.nodeInfo.nodeEndHour?.completeDate ??
      sd.servicePickupDateRangeEnd.completeDate ??
      startM.clone().add(30, 'minutes').toISOString();

    const cleanedEnd = rawEndIso.split(' ')[0];
    const endM = moment.tz(cleanedEnd, this.timeZone);

    this.assignForm.patchValue({
      nodeDate: startM.format('YYYY-MM-DD'),
      nodeStartHour: startM.format('HH:mm'),
      nodeEndHour: endM.format('HH:mm'),
    });
  }

  closeCanvas(): void {
    this.offcanvasService.dismiss();
  }

  editNodeDateTime(): void {
    if (this.assignForm.invalid || this.loading) {
      return;
    }

    const zone = this.zones.find((z) => z.zoneId === this.serviceInfo.zoneId);
    if (!zone) {
      const msg = this.translate.instant(
        'detail_service.modals.modal-gestion-node.node_updated_missing_data'
      );
      this.alerts.open(msg, { status: 'error', autoClose: true }).subscribe();
      return;
    }

    const { nodeDate, nodeStartHour, nodeEndHour } = this.assignForm.value;
    this.loading = true;

    const startMoment = moment.tz(
      `${nodeDate} ${nodeStartHour}`,
      'YYYY-MM-DD HH:mm',
      zone.timeZone
    );
    const endMoment = moment.tz(
      `${nodeDate} ${nodeEndHour}`,
      'YYYY-MM-DD HH:mm',
      zone.timeZone
    );

    const start = `${startMoment.format()} ${zone.timeZone}`;
    const end = `${endMoment.format()} ${zone.timeZone}`;

    const payload: UpdateAssignedTimeRequest = {
      orderHash: this.serviceInfo.orderHash,
      nodeId: this.nodeInfo.nodeId,
      desiredAssignedStartTime: { completeDate: start },
      desiredAssignedEndTime: { completeDate: end },
    };

    this.hs
      .updateServiceAssignedHour(payload)
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res) => this.onAssignSuccess(res),
        error: (err) => this.onAssignError(err),
      });
  }

  private onAssignSuccess(res: UpdateResponse): void {
    const key = res.wasSuccessful
      ? 'detail_service.modals.modal-gestion-node.node_updated_success'
      : 'detail_service.modals.modal-gestion-node.node_updated_error';

    this.alerts
      .open(this.translate.instant(key), {
        status: res.wasSuccessful ? 'success' : 'error',
        autoClose: true,
      })
      .subscribe();

    if (res.wasSuccessful) {
      this.updated.emit(true);
      this.closeCanvas();
    }
  }

  private onAssignError(err: any): void {
    const key =
      err.status === 500
        ? 'detail_service.modals.modal-gestion-node.server_error'
        : 'detail_service.modals.modal-gestion-node.node_updated_error';

    this.alerts
      .open(this.translate.instant(key, { error: err.message }), {
        status: 'error',
        autoClose: false,
      })
      .subscribe();
  }

  private applyDurationToEnd(start: string): void {
    const date = this.assignForm.value.nodeDate;
    const fmt = 'YYYY-MM-DD HH:mm';
    const startM = moment.tz(`${date} ${start}`, fmt, this.timeZone);
    const newEnd = startM.clone().add(this.initialDurationMinutes, 'minutes');

    this.assignForm.patchValue(
      { nodeEndHour: newEnd.format('HH:mm') },
      { emitEvent: false }
    );
  }

  formatHour(controlName: 'nodeStartHour' | 'nodeEndHour'): void {
    const ctrl = this.assignForm.get(controlName);
    if (!ctrl) return;
    let v = ((ctrl.value as string) || '').replace(/\D/g, '');
    if (v.length > 2) {
      v = v.slice(0, 2) + ':' + v.slice(2, 4);
    }
    ctrl.setValue(v, { emitEvent: false });
  }

  private startBeforeEndValidator(group: FormGroup): ValidationErrors | null {
    const start = group.get('nodeStartHour')?.value as string;
    const end = group.get('nodeEndHour')?.value as string;
    if (hhmmPattern.test(start) && hhmmPattern.test(end) && end <= start) {
      return { timeRange: true };
    }
    return null;
  }
}
